import * as React from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { TowingEquipmentAdmin } from "../../../../hooks/useTowingEquipmentAdmin";
import { DimensionInput } from "../../MovableObstacleAdmin/components/DimensionInput";
import { EditFormData } from "../index";

interface Props {
  open: boolean;
  equipment: TowingEquipmentAdmin | null;
  isCreating: boolean;
  onClose: () => void;
  onSave: (data: EditFormData) => Promise<void>;
}

export const EquipmentEditDialog: React.FC<Props> = ({
  open,
  equipment,
  isCreating,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = React.useState<EditFormData>({
    name: "",
    width: "",
    length: "",
    height: "",
    tow_bar_length: "",
    no_footprint: false,
  });

  // Reset form when equipment changes or creating new
  React.useEffect(() => {
    if (isCreating) {
      setFormData({
        name: "",
        width: "",
        length: "",
        height: "",
        tow_bar_length: "",
        no_footprint: false,
      });
    } else if (equipment) {
      setFormData({
        name: equipment.name,
        width: "",
        length: "",
        height: "",
        tow_bar_length: "",
        no_footprint: equipment.no_footprint ?? false,
      });
    }
  }, [equipment, isCreating]);

  const handleChange = (field: keyof EditFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    await onSave(formData);
    // Don't close here - let parent close on success
  };

  const handleClose = () => {
    setFormData({
      name: "",
      width: "",
      length: "",
      height: "",
      tow_bar_length: "",
      no_footprint: false,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isCreating ? "Add New Towing Equipment" : "Edit Towing Equipment"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder={equipment?.name || "Equipment name"}
            autoFocus
            required={isCreating}
          />
          <Divider />

          {(equipment || isCreating) && (
            <>
              <DimensionInput
                label="Width"
                currentValue={equipment?.width}
                value={formData.width}
                onChange={(value) => handleChange('width', value)}
                placeholder={isCreating ? "Width in feet" : "New width"}
                required={isCreating}
              />
              <DimensionInput
                label="Length"
                currentValue={equipment?.length}
                value={formData.length}
                onChange={(value) => handleChange('length', value)}
                placeholder={isCreating ? "Length in feet" : "New length"}
                required={isCreating}
              />
              <DimensionInput
                label="Height"
                currentValue={equipment?.height}
                value={formData.height}
                onChange={(value) => handleChange('height', value)}
                placeholder={isCreating ? "Height in feet" : "New height"}
                required={isCreating}
              />
            </>
          )}

          <Divider />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.no_footprint}
                onChange={(e) => handleChange('no_footprint', e.target.checked)}
              />
            }
            label="No Footprint (Robotic)"
          />
          <Typography variant="caption" color="textSecondary">
            Check if equipment has no physical footprint (affects collision detection only)
          </Typography>

          <Divider />

          <DimensionInput
            label="Tow Bar"
            currentValue={equipment?.tow_bar_length ?? undefined}
            value={formData.tow_bar_length}
            onChange={(value) => handleChange('tow_bar_length', value)}
            placeholder={isCreating ? "Tow bar length (optional)" : "New tow bar length"}
            required={false}
          />
        </Stack>
        {!isCreating && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            * Leave fields empty to keep current values
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            isCreating && (
              !formData.name ||
              !formData.width ||
              !formData.length ||
              !formData.height
            )
          }
        >
          {isCreating ? "Create" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
